/**
 * Kündigungsbutton nach § 312k BGB (#271, seit 16.10.2026).
 *
 * Ohne Anmeldung erreichbar: „Verträge hier kündigen“ steht in der Fußzeile
 * der Website, in der Fußzeile der App und auf der Karte des laufenden Plans.
 * Die Seite fragt nur, was das Gesetz verlangt (§ 312k Abs. 2 Nr. 1): Name,
 * Konto, Vertrag, Art und Grund, Zeitpunkt, Adresse für die Bestätigung.
 * Keine Angebote zum Bleiben, keine Pause (BGH I ZR 200/25). Mit Sitzung sind
 * Konto und Adresse vorausgefüllt, und der Server ordnet über die Sitzung zu.
 *
 * Nach dem Absenden lässt sich die Erklärung mit Datum und Uhrzeit speichern
 * (§ 312k Abs. 3): als Textdatei oder gedruckt. Die Bestätigung per Mail
 * schickt der Server (kuendigung.ts).
 *
 * Englisch (seit 16.10.2026): Seite „Cancel contracts here“, Knopf „Cancel
 * now“, die Erklärung und die Datei in der Sprache der Seite, mit denselben
 * Wendungen wie die englische Eingangsmail (kuendigung.ts).
 */

import { useState, type FormEvent } from 'react';
import { sendeKuendigung, type KuendigungAngaben, type KuendigungAntwort } from '../api';
import { useSitzung } from '../session';
import { Feld, Hinweis, Knopf, Rahmen } from '../ui';
import { locale, sprache, t } from '../sprache';

const SUPPORT = 'run@99takes.com';
const VERTRAG = '99takes Plan (Abo)';
const VERTRAG_EN = '99takes plan (subscription)';

const zeitpunkt = (iso: string) => {
  const z = new Date(iso).toLocaleString(locale(), {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin',
  });
  return t(`${z} Uhr`, z);
};
const tag = (iso: string) => new Date(iso).toLocaleDateString(locale(), { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });

/** Der Wortlaut der Erklärung, wie er auf der Seite, in der Datei und in der Mail steht. */
export function erklaerung(a: KuendigungAngaben, am: string): string {
  if (sprache() === 'en') {
    const wannEn = a.zeitpunkt === 'naechstmoeglich' ? 'at the earliest possible date' : `as of ${tag(`${a.zeitpunkt}T12:00:00Z`)}`;
    return [
      'Notice of termination to Tappla UG (haftungsbeschränkt), 99takes',
      '',
      `I hereby terminate the contract “${a.vertrag}” ${wannEn} (${a.art === 'ausserordentlich' ? 'extraordinary' : 'ordinary'} termination).`,
      ...(a.grund ? [`Reason: ${a.grund}`] : []),
      '',
      `Name: ${a.name}`,
      `Account (email): ${a.konto_email ?? ''}`,
      `Confirmation to: ${a.bestaetigung_an}`,
      '',
      `Submitted via the “Cancel now” button on run.99takes.com/kuendigen on ${zeitpunkt(am)} (Berlin time).`,
    ].join('\n');
  }
  const wann = a.zeitpunkt === 'naechstmoeglich' ? 'zum nächstmöglichen Zeitpunkt' : `zum ${tag(`${a.zeitpunkt}T12:00:00Z`)}`;
  return [
    'Kündigungserklärung an die Tappla UG (haftungsbeschränkt), 99takes',
    '',
    `Hiermit kündige ich den Vertrag „${a.vertrag}“ ${wann} (${a.art === 'ausserordentlich' ? 'außerordentliche' : 'ordentliche'} Kündigung).`,
    ...(a.grund ? [`Grund: ${a.grund}`] : []),
    '',
    `Name: ${a.name}`,
    `Konto (E-Mail): ${a.konto_email ?? ''}`,
    `Bestätigung an: ${a.bestaetigung_an}`,
    '',
    `Abgegeben über die Schaltfläche „Jetzt kündigen“ auf run.99takes.com/kuendigen am ${zeitpunkt(am)} (Berlin).`,
  ].join('\n');
}

const feldKlasse = `box-border w-full rounded-feld border border-linie-feld bg-flaeche px-3 py-2.5 text-[0.9375rem] text-text
  outline-none transition-[border-color,box-shadow] duration-150 hover:border-linie-stark
  focus:border-marke focus:shadow-[0_0_0_3px_rgb(63_138_135/0.16)]`;

export default function Kuendigen() {
  const { session, status } = useSitzung();
  const angemeldetAls = status === 'angemeldet' ? session?.user?.email ?? null : null;

  const [name, setName] = useState('');
  const [kontoEmail, setKontoEmail] = useState('');
  const [art, setArt] = useState<'ordentlich' | 'ausserordentlich'>('ordentlich');
  const [grund, setGrund] = useState('');
  const [wann, setWann] = useState<'naechstmoeglich' | 'datum'>('naechstmoeglich');
  const [datum, setDatum] = useState('');
  const [bestaetigungAn, setBestaetigungAn] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState<{ angaben: KuendigungAngaben; antwort: KuendigungAntwort } | null>(null);

  const konto = angemeldetAls ?? kontoEmail;

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    const angaben: KuendigungAngaben = {
      name: name.trim(),
      konto_email: konto.trim(),
      vertrag: t(VERTRAG, VERTRAG_EN),
      art,
      ...(art === 'ausserordentlich' ? { grund: grund.trim() } : {}),
      zeitpunkt: wann === 'datum' && datum ? datum : 'naechstmoeglich',
      bestaetigung_an: (bestaetigungAn || konto).trim(),
    };
    setSendet(true);
    try {
      const antwort = await sendeKuendigung(angaben);
      setFertig({ angaben, antwort });
      window.scrollTo({ top: 0 });
    } catch (x) {
      setFehler(x instanceof Error ? x.message : String(x));
    } finally {
      setSendet(false);
    }
  }

  if (fertig) {
    const { angaben, antwort } = fertig;
    const text = erklaerung(angaben, antwort.eingegangen_am);
    const speichern = () => {
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('kuendigung', 'cancellation')}-99takes-${antwort.eingegangen_am.slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <Rahmen
        titel={t('Deine Kündigung ist eingegangen', 'Your cancellation has been received')}
        unter={t(`Eingegangen am ${zeitpunkt(antwort.eingegangen_am)}.`, `Received on ${zeitpunkt(antwort.eingegangen_am)}.`)}
      >
        {antwort.ergebnis === 'gekuendigt' && (
          <Hinweis art="gut">{t(
            `Dein Plan ist gekündigt${antwort.endet_am ? ` und endet am ${tag(antwort.endet_am)}` : ''}. Danach wird nichts mehr abgebucht.`,
            `Your plan is canceled${antwort.endet_am ? ` and ends on ${tag(antwort.endet_am)}` : ''}. Nothing will be charged after that.`,
          )}</Hinweis>
        )}
        {antwort.ergebnis === 'schon_gekuendigt' && (
          <Hinweis art="gut">{t(
            `Dein Plan war schon gekündigt${antwort.endet_am ? ` und endet am ${tag(antwort.endet_am)}` : ''}.`,
            `Your plan was already canceled${antwort.endet_am ? ` and ends on ${tag(antwort.endet_am)}` : ''}.`,
          )}</Hinweis>
        )}
        {antwort.ergebnis === 'kein_abo' && <Hinweis>{t('In deinem Konto läuft kein Plan. Es gibt nichts zu kündigen, und es wird nichts abgebucht.', 'There is no plan running in your account. There is nothing to cancel, and nothing will be charged.')}</Hinweis>}
        <p className="mt-0 mb-4 text-[0.9375rem] leading-relaxed text-text">
          {antwort.mail === 'verschickt'
            ? t(<>Eine Bestätigung mit Inhalt, Datum und Uhrzeit ist unterwegs an <strong className="font-semibold">{angaben.bestaetigung_an}</strong>.</>,
              <>A confirmation with content, date and time is on its way to <strong className="font-semibold">{angaben.bestaetigung_an}</strong>.</>)
            : t(<>Die Bestätigung per Mail ließ sich gerade nicht verschicken. Die Kündigung gilt trotzdem; speichere sie unten, und wir schicken die Bestätigung nach.</>,
              <>The confirmation email could not be sent just now. The cancellation is still valid. Save it below, and we will send the confirmation later.</>)}
          {antwort.ergebnis === 'eingegangen' && t(' Wann der Vertrag endet, schreiben wir an die E-Mail-Adresse des Kontos.', ' We will send the date your contract ends to the email address of the account.')}
        </p>
        <pre className="m-0 mb-4 overflow-x-auto rounded-kasten border border-linie bg-ebene p-3.5 font-sans text-[0.8125rem] leading-relaxed whitespace-pre-wrap text-text">{text}</pre>
        <div className="flex flex-wrap gap-2 print:hidden">
          <button type="button" onClick={speichern} className="cursor-pointer rounded-full border border-linie bg-flaeche px-4 py-2 text-[0.875rem] font-semibold text-text hover:border-linie-stark">
            {t('Als Datei speichern', 'Save as file')}
          </button>
          <button type="button" onClick={() => window.print()} className="cursor-pointer rounded-full border border-linie bg-flaeche px-4 py-2 text-[0.875rem] font-semibold text-text hover:border-linie-stark">
            {t('Drucken', 'Print')}
          </button>
        </div>
      </Rahmen>
    );
  }

  return (
    <Rahmen
      titel={t('Vertrag kündigen', 'Cancel contracts here')}
      unter={t('Hier kündigst du deinen Plan bei 99takes, auch ohne Anmeldung.', 'Cancel your 99takes plan here, also without signing in.')}
      fuss={t(<>Du kannst auch per E-Mail an <a href={`mailto:${SUPPORT}`} className="text-marke-tief underline">{SUPPORT}</a> kündigen.</>,
        <>You can also cancel by email to <a href={`mailto:${SUPPORT}`} className="text-marke-tief underline">{SUPPORT}</a>.</>)}
    >
      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
      <form onSubmit={absenden} noValidate={false}>
        <Feld label={t('Dein Name', 'Your name')} name="name" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} />
        {angemeldetAls ? (
          <Feld label={t('E-Mail-Adresse des Kontos', 'Email address of the account')} type="email" value={angemeldetAls} readOnly disabled />
        ) : (
          <Feld
            label={t('E-Mail-Adresse des Kontos', 'Email address of the account')} type="email" name="konto_email" autoComplete="email" required
            hinweis={t('Die Adresse, mit der du bei 99takes angemeldet bist.', 'The address you use to sign in to 99takes.')}
            value={kontoEmail} onChange={e => setKontoEmail(e.target.value)}
          />
        )}
        <Feld label={t('Vertrag', 'Contract')} value={t(VERTRAG, VERTRAG_EN)} readOnly disabled />

        <fieldset className="m-0 mb-4 border-0 p-0">
          <legend className="mb-1.5 block p-0 text-[0.84375rem] font-medium text-text">{t('Art der Kündigung', 'Type of cancellation')}</legend>
          <label className="mb-1.5 flex cursor-pointer items-center gap-2 text-[0.9375rem] text-text">
            <input type="radio" name="art" checked={art === 'ordentlich'} onChange={() => setArt('ordentlich')} className="accent-marke" />
            {t('Ordentliche Kündigung', 'Ordinary cancellation')}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-[0.9375rem] text-text">
            <input type="radio" name="art" checked={art === 'ausserordentlich'} onChange={() => setArt('ausserordentlich')} className="accent-marke" />
            {t('Außerordentliche Kündigung aus wichtigem Grund', 'Extraordinary cancellation for good cause')}
          </label>
          {art === 'ausserordentlich' && (
            <label className="mt-3 block">
              <span className="mb-1.5 block text-[0.84375rem] font-medium text-text">{t('Grund', 'Reason')}</span>
              <textarea required rows={3} maxLength={2000} value={grund} onChange={e => setGrund(e.target.value)} className={feldKlasse} />
            </label>
          )}
        </fieldset>

        <fieldset className="m-0 mb-4 border-0 p-0">
          <legend className="mb-1.5 block p-0 text-[0.84375rem] font-medium text-text">{t('Zeitpunkt', 'Date')}</legend>
          <label className="mb-1.5 flex cursor-pointer items-center gap-2 text-[0.9375rem] text-text">
            <input type="radio" name="wann" checked={wann === 'naechstmoeglich'} onChange={() => setWann('naechstmoeglich')} className="accent-marke" />
            {t('Zum nächstmöglichen Zeitpunkt', 'At the earliest possible date')}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-[0.9375rem] text-text">
            <input type="radio" name="wann" checked={wann === 'datum'} onChange={() => setWann('datum')} className="accent-marke" />
            {t('Zu einem bestimmten Tag', 'On a specific day')}
          </label>
          {wann === 'datum' && (
            <input type="date" required value={datum} onChange={e => setDatum(e.target.value)} className={`${feldKlasse} mt-3 h-[2.75rem]`} aria-label={t('Tag', 'Day')} />
          )}
        </fieldset>

        <Feld
          label={t('Bestätigung an', 'Confirmation to')} type="email" name="bestaetigung_an" autoComplete="email"
          hinweis={t('An diese Adresse schicken wir die Bestätigung mit Datum und Uhrzeit. Leer: an die Adresse des Kontos.', 'We send the confirmation with date and time to this address. Empty: to the address of the account.')}
          placeholder={konto || undefined}
          value={bestaetigungAn} onChange={e => setBestaetigungAn(e.target.value)}
        />
        <Knopf type="submit" laedt={sendet}>{t('Jetzt kündigen', 'Cancel now')}</Knopf>
      </form>
    </Rahmen>
  );
}
